import type { McpServerConfig, McpServerView } from '@omni/shared';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, ErrorText, Spinner } from '../components/ui';
import { zh } from '../i18n';

/**
 * MCP JSON editor (CONTRACTS §4.2).
 *
 * Secrets never round-trip through the browser: the GET returns every secret
 * value as the literal `__OMNI_KEEP__`, and sending that back is what tells the
 * gateway to keep the stored ciphertext (R10). So the honest edit model is
 * "edit the masked document and save it" — the mask IS the value here, and the
 * hint says so rather than letting someone believe they are looking at a key.
 */

const KEEP = '__OMNI_KEEP__';

function viewToConfig(view: McpServerView): McpServerConfig {
  if (view.transport === 'stdio') {
    return {
      type: 'stdio',
      command: view.command ?? '',
      ...(view.args ? { args: view.args } : {}),
      ...(view.env ? { env: view.env } : {}),
    };
  }
  return {
    type: view.transport,
    url: view.url ?? '',
    ...(view.headers ? { headers: view.headers } : {}),
  };
}

function statusBadge(view: McpServerView): JSX.Element {
  if (view.status === 'connected') return <Badge tone="success">{zh.settings.mcpStatusConnected}</Badge>;
  if (view.status === 'failed') return <Badge tone="danger">{zh.settings.mcpStatusFailed}</Badge>;
  return <Badge tone="muted">{zh.settings.mcpStatusUnknown}</Badge>;
}

export interface McpEditorProps {
  readonly servers: Record<string, McpServerView> | undefined;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly saveError: unknown;
  readonly onSave: (servers: Record<string, McpServerConfig>) => void;
}

export function McpEditor({
  servers,
  loading,
  saving,
  saveError,
  onSave,
}: McpEditorProps): JSX.Element {
  const serialized = useMemo(() => {
    if (!servers) return '{}';
    const out: Record<string, McpServerConfig> = {};
    for (const [name, view] of Object.entries(servers)) out[name] = viewToConfig(view);
    return JSON.stringify(out, null, 2);
  }, [servers]);

  const [draft, setDraft] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(null);
  }, [serialized]);

  const text = draft ?? serialized;
  const entries = servers ? Object.entries(servers) : [];

  const save = (): void => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setParseError(zh.settings.mcpInvalidJson);
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setParseError(zh.settings.mcpInvalidJson);
      return;
    }
    setParseError(null);
    onSave(parsed as Record<string, McpServerConfig>);
  };

  return (
    <Card>
      <h2>{zh.settings.sectionMcp}</h2>
      <p className="small muted">{zh.settings.mcpHint}</p>

      {loading ? <Spinner /> : null}

      {entries.length === 0 && !loading ? (
        <p className="small muted">{zh.settings.mcpEmpty}</p>
      ) : (
        <ul className="list" style={{ marginBottom: '0.5rem' }}>
          {entries.map(([name, view]) => (
            <li key={name} className="row" style={{ padding: '0.35rem 0' }}>
              <span className="mono">{name}</span>
              {statusBadge(view)}
              {view.hasSecret ? <Badge tone="warn">{zh.settings.mcpHasSecret}</Badge> : null}
              {view.toolCount !== null ? (
                <span className="tiny faint">{zh.settings.mcpToolCount(view.toolCount)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <textarea
        className="editor"
        aria-label={zh.settings.sectionMcp}
        spellCheck={false}
        value={text}
        onChange={(event) => setDraft(event.target.value)}
      />
      <p className="tiny faint">
        {zh.settings.mcpMaskHint} <code className="mono">{KEEP}</code>
      </p>

      {parseError ? (
        <p className="field__error" role="alert">
          {parseError}
        </p>
      ) : null}
      <ErrorText error={saveError} />

      <Button variant="primary" disabled={saving} onClick={save}>
        {saving ? zh.app.saving : zh.app.save}
      </Button>
    </Card>
  );
}
